import { useEffect, useRef, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { Close, CloseRounded, SearchRounded } from '@mui/icons-material';
import { ShowPreferenceRow } from './SettingsPreferenceComponents';
import { searchShows, showSearchAliases, showMutationFor, showPreferenceStatus } from '../utils/showSearch';

const SearchShowsDrawer = ({
    open,
    onClose,
    onOpenShow,
    onSaveShow,
    onResults,
    mutations = {},
    savedShows = {},
    initialState,
    ready,
}) => {
    const [query, setQuery] = useState('');
    const [shows, setShows] = useState([]);
    const [resultsQuery, setResultsQuery] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [retry, setRetry] = useState(0);
    const inputRef = useRef(null);
    const scrollRef = useRef(null);
    const restoreScrollRef = useRef(null);
    const onResultsRef = useRef(onResults);
    onResultsRef.current = onResults;
    const trimmedQuery = query.trim();

    useEffect(() => {
        if (!open) return;
        setQuery(initialState?.query || '');
        setShows(initialState?.shows || []);
        setResultsQuery(null);
        setError(false);
        restoreScrollRef.current = initialState?.scrollTop || 0;
        const timer = window.setTimeout(() => inputRef.current?.focus(), 100);
        return () => window.clearTimeout(timer);
    }, [open, initialState]);

    useEffect(() => {
        if (!open || !trimmedQuery) {
            setLoading(false);
            setShows([]);
            setResultsQuery(null);
            setError(false);
            return;
        }
        let active = true;
        const controller = new AbortController();
        setLoading(true);
        setError(false);
        const timer = window.setTimeout(async () => {
            try {
                await ready;
                if (!active) return;
                const results = await searchShows(trimmedQuery, controller.signal);
                if (!active) return;
                setShows(results);
                setResultsQuery(trimmedQuery);
                onResultsRef.current?.(results);
            } catch (err) {
                if (active) {
                    setError(true);
                    setResultsQuery(trimmedQuery);
                }
            } finally {
                if (active) setLoading(false);
            }
        }, 250);
        return () => {
            active = false;
            controller.abort();
            window.clearTimeout(timer);
        };
    }, [open, trimmedQuery, retry, ready]);

    useEffect(() => {
        if (!loading && resultsQuery === trimmedQuery && restoreScrollRef.current !== null) {
            if (scrollRef.current) scrollRef.current.scrollTop = restoreScrollRef.current;
            restoreScrollRef.current = null;
        }
    }, [loading, resultsQuery, trimmedQuery]);

    const openShow = (show) => {
        onOpenShow(show, { query, shows, scrollTop: scrollRef.current?.scrollTop || 0 });
    };

    const searching = loading || (Boolean(trimmedQuery) && resultsQuery !== trimmedQuery);
    const changeQuery = (value) => {
        restoreScrollRef.current = null;
        setQuery(value);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    };

    return (
        <Drawer anchor='bottom' open={open} onClose={onClose}
            PaperProps={{ role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'add-shows-title' }}>
            <div className='appDrawerSurface addShowsDrawer'>
                <div className='addShowsHeader'>
                    <h2 id='add-shows-title'>Add Shows</h2>
                    <button type='button' aria-label='Close Add Shows' onClick={onClose} className='addShowsClose'>
                        <Close />
                    </button>
                </div>
                <div className='appSearchBoxField addShowsSearch' role='search'>
                    <SearchRounded className='appSearchBoxLeadingIcon' aria-hidden='true' />
                    <input ref={inputRef} className='appSearchBoxInput' type='search'
                        aria-label='Search shows' placeholder='Search shows'
                        value={query} onChange={(event) => changeQuery(event.target.value)} />
                    {query && (
                        <button type='button' className='appSearchBoxClearButton' aria-label='Clear search'
                            onClick={() => {
                                changeQuery('');
                                inputRef.current?.focus();
                            }}>
                            <CloseRounded fontSize='small' aria-hidden='true' />
                        </button>
                    )}
                </div>
                <div className='addShowsResults' ref={scrollRef}>
                    {!trimmedQuery ? (
                        <div className='addShowsEmptyState'>
                            <SearchRounded className='addShowsEmptyIcon' aria-hidden='true' />
                            <h3>Find your next show</h3>
                            <p>Search by name to get started.</p>
                        </div>
                    ) : searching ? (
                        <p className='addShowsMessage' role='status'>Searching shows…</p>
                    ) : error ? (
                        <div className='addShowsMessage' role='alert'>
                            <p>Couldn’t load shows.</p>
                            <button type='button' onClick={() => setRetry((value) => value + 1)}>Retry search</button>
                        </div>
                    ) : shows.length === 0 ? (
                        <p className='addShowsMessage'>No shows matching “{trimmedQuery}”.</p>
                    ) : (
                        <div className='settingsPreferenceRows'>
                            {shows.map((result) => {
                                const aliasSave = showSearchAliases(result).map((key) => savedShows[key]).find(Boolean);
                                const saved = aliasSave && (savedShows[`source:${aliasSave.source.id}`] || aliasSave);
                                const resolvedShow = saved ? { ...result, source: saved.source, subscribeStatus: saved.subscribeStatus } : result;
                                const mutation = showMutationFor(mutations, resolvedShow);
                                const show = { ...resolvedShow,
                                    subscribeStatus: showPreferenceStatus(resolvedShow.subscribeStatus, mutation) };
                                return (
                                    <ShowPreferenceRow
                                        key={result.key}
                                        source={show}
                                        description={show.creator}
                                        openLabel={`Open ${show.name}`}
                                        onOpen={() => openShow(show)}
                                        status={show.subscribeStatus}
                                        mutation={mutation}
                                        onStatusChange={(status) => onSaveShow(show, status)}
                                        onRetry={() => onSaveShow(show, mutation.status)}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className='addShowsFooter'>
                    <button type='button' className='closeFooterButton' onClick={onClose}>Done</button>
                </div>
            </div>
        </Drawer>
    );
};

export default SearchShowsDrawer;
